import { ChangeEvent, useState, useRef, useEffect } from 'react';
import { Keypair, Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { useClipboard } from '../hooks/useClipboard';
import { useSolana } from '../hooks/useSolana';
import parseCsv from './parse-csv';

import { removeKeypairDuplicates } from './remove-keypair-duplicates';
import { LiquidityPoolKeysV4 } from '@raydium-io/raydium-sdk';
import bs58 from 'bs58';

export function LoadWallet({
    connection,
    wallet,
    setWallet,
    walletLoaded,
    setWalletLoaded,
    setFunder,
    setBundleBuyKeypairs,
    setBundleBuyValues,
    setHolderKeypairs,
    setMixKeypairs,
    setFreezeKeypairs,
    setAllUniqueWallets,
    setAllWallets,
    setAllWalletRoles,
    setInitialTotalSolBalance,
    deployedToken,
    setDeployedToken,
    csvFile,
    setCsvFile,
    setParsedCsvData,
    setVolumeBuyWallets,
    setVolumeBuyValues,
    logMessage,
    deployedTokenName,
    setDeployedTokenName,
    deployedTokenSymbol,
    setDeployedTokenSymbol,
    csvUrlParam,
    children,
    poolBalance,
    totalWalletsBalance,
    setTotalWalletsBalance,
    setVolFunder,
    deployer,
    allWallets,
    volumeBuyKeypairs,
    setPoolKeys,
    setDeployedMarketId,
    injectToken,
    currentSlot,
}: {
    connection: Connection;
    wallet: Keypair;
    setWallet: React.Dispatch<React.SetStateAction<Keypair>>;
    walletLoaded: boolean;
    setWalletLoaded: React.Dispatch<React.SetStateAction<boolean>>;
    setFunder: React.Dispatch<React.SetStateAction<Keypair>>;
    setBundleBuyKeypairs: React.Dispatch<React.SetStateAction<Keypair[]>>;
    setBundleBuyValues: React.Dispatch<React.SetStateAction<number[]>>;
    setHolderKeypairs: React.Dispatch<React.SetStateAction<Keypair[]>>;
    setMixKeypairs: React.Dispatch<React.SetStateAction<Keypair[]>>;
    setFreezeKeypairs: React.Dispatch<React.SetStateAction<Keypair[]>>;
    setAllUniqueWallets: React.Dispatch<React.SetStateAction<Keypair[]>>;
    setAllWallets: React.Dispatch<React.SetStateAction<Keypair[]>>;
    setAllWalletRoles: React.Dispatch<React.SetStateAction<string[]>>;
    setInitialTotalSolBalance: React.Dispatch<React.SetStateAction<number>>;
    deployedToken: string;
    setDeployedToken: React.Dispatch<React.SetStateAction<string>>;
    csvFile: File | null;
    setCsvFile: React.Dispatch<React.SetStateAction<File | null>>;
    setParsedCsvData: React.Dispatch<React.SetStateAction<any[][] | null>>;
    setVolumeBuyWallets: React.Dispatch<React.SetStateAction<Keypair[]>>;
    setVolumeBuyValues: React.Dispatch<React.SetStateAction<number[]>>;
    deployedTokenName: string;
    setDeployedTokenName: React.Dispatch<React.SetStateAction<string>>;
    deployedTokenSymbol: string;
    setDeployedTokenSymbol: React.Dispatch<React.SetStateAction<string>>;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    csvUrlParam: string;
    children: React.ReactNode;
    poolBalance: string;
    totalWalletsBalance: number;
    setTotalWalletsBalance: React.Dispatch<React.SetStateAction<number>>;
    setVolFunder: React.Dispatch<React.SetStateAction<Keypair | undefined>>;
    deployer: Keypair;
    allWallets: Keypair[];
    volumeBuyKeypairs: Keypair[];
    setPoolKeys: React.Dispatch<React.SetStateAction<LiquidityPoolKeysV4 | undefined>>;
    setDeployedMarketId: React.Dispatch<React.SetStateAction<string | undefined>>;
    injectToken: (mint: string) => void;
    currentSlot: number;
}) {
    const { isCopied: isLoadedWalletCopied, copyToClipboard: copyLoadedWallet } = useClipboard();
    const { isCopied: isTokenDeployerWalletCopied, copyToClipboard: copyTokenDeployerWallet } = useClipboard();
    const { isCopied: isMintedTokenCopied, copyToClipboard: copyMintedToken } = useClipboard();

    const { getMultipleSolBalances } = useSolana();
    // Handle CSV File
    const csvFileInput = useRef<HTMLInputElement>(null);
    const [solBalance, setSolBalance] = useState<number>(0);

    useEffect(() => {
        async function updateSolBalance() {
            try {
                const walletSolBalance = await connection.getBalance(deployer.publicKey, 'processed');
                setSolBalance(walletSolBalance);

                const totalSolBalance = await getMultipleSolBalances(
                    connection,
                    removeKeypairDuplicates([...allWallets, ...volumeBuyKeypairs]).map((keypair) => keypair.publicKey),
                );

                setTotalWalletsBalance(totalSolBalance.reduce((a, b) => a + b, 0));
            } catch (e) {
                logMessage(`Error while fetching wallet balance, ${e}`);
            }
        }

        updateSolBalance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wallet, allWallets, volumeBuyKeypairs, connection, deployer, currentSlot]);

    const csvFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        parseCsvAndSetWallets(file!);
    };

    async function parseCsvAndSetWallets(file: File) {
        setCsvFile(file);
        if (file && file.type === 'text/csv') {
            const parsedCSV = await parseCsv(file);
            console.log('Parsed CSV:', parsedCSV);
            const trimmedRows = parsedCSV.map((row) => row.map((item) => (typeof item === 'string' ? item.trim() : item)));
            const deployer: Keypair[] = [];
            const funders: Keypair[] = [];
            const volFunders: Keypair[] = [];
            const bundleBuyKeypairs: Keypair[] = [];
            const bundleBuyValues: number[] = [];
            const holderKeypairs: Keypair[] = [];
            const mixKeypairs: Keypair[] = [];
            const freezeKeypairs: Keypair[] = [];
            const volumeBuyKeypairs: Keypair[] = [];
            const volumeBuyValues: number[] = [];
            const bundleBuyStrings: string[] = [];

            for (let index = 0; index < trimmedRows.length; index++) {
                const [key, walletRole, amount] = trimmedRows[index];

                let wallet: Keypair;
                try {
                    wallet = Keypair.fromSecretKey(bs58.decode(key));
                } catch {
                    const buffer = Buffer.from(key, 'hex');
                    const base58Privkey = bs58.encode(new Uint8Array(buffer));
                    wallet = Keypair.fromSecretKey(bs58.decode(base58Privkey));
                    console.log('Using hex private key format');
                }

                amount === '' ? bundleBuyStrings.push('-') : bundleBuyStrings.push(amount);
                switch (walletRole) {
                    case 'DEPLOYER':
                        deployer.push(wallet);
                        break;
                    case 'FUNDER':
                        funders.push(wallet);
                        break;
                    case 'VOLFUNDER':
                        volFunders.push(wallet);
                        break;
                    case 'BUNDLEBUY':
                        bundleBuyValues.push(Number(amount));
                        bundleBuyKeypairs.push(wallet);
                        break;
                    case 'HOLDER':
                        holderKeypairs.push(wallet);
                        break;
                    case 'MIX':
                        mixKeypairs.push(wallet);
                        break;
                    case 'FREEZER':
                        freezeKeypairs.push(wallet);
                        break;
                    case 'VOLUMEBUY': //not displayed type
                        volumeBuyKeypairs.push(wallet);
                        volumeBuyValues.push(Number(amount));
                        break;

                    default:
                        return logMessage('Unknown wallet role detected');
                }
            }

            if (deployer.length !== 1) {
                logMessage('Should have exactly one deployer wallet', 'error');
                return;
            }
            if (funders.length > 1) {
                logMessage('Should have not more than one funder wallet', 'error');
                return;
            }
            if (funders.length < 1) {
                logMessage("Funder not found, using deployer's wallet as funder");
                funders.push(deployer[0]);
            }
            if (bundleBuyKeypairs.length > 32) {
                logMessage('Should have not more than 32 bundlebuy wallets', 'error');
                return;
            }
            if (freezeKeypairs.length > 1) {
                logMessage('Should have not more than one freeze wallet', 'error');
                return;
            }
            if (freezeKeypairs.length < 1) {
                logMessage('Freeze wallet not found, using deployer wallet as a freeze wallet');
                freezeKeypairs.push(deployer[0]);
            }
            if (holderKeypairs.length > 1) {
                logMessage('Should have not more than one holder wallet', 'error');
                return;
            }
            if (volFunders.length > 1) {
                logMessage('Should have not more than one volume funder wallet', 'error');
                return;
            }
            if (volFunders.length === 0 && volumeBuyKeypairs.length > 0) {
                logMessage('Volume funder not found, when volume buy wallets are present', 'error');
                return;
            }

            //check if mix wallets have dubplicated public keys
            const mixPublicKeys = mixKeypairs.map((keypair) => keypair.publicKey.toBase58());
            const mixUniquePublicKeys = new Set(mixPublicKeys);
            if (mixPublicKeys.length !== mixUniquePublicKeys.size) {
                logMessage('Mix wallets contain duplicates');
                return;
            }
            const allWallets = deployer.concat(funders, volFunders, bundleBuyKeypairs, holderKeypairs, freezeKeypairs, mixKeypairs);
            //put the number of roles equal to the number of wallets
            const roles = [
                ...new Array(deployer.length).fill('DEPLOYER'),
                ...new Array(funders.length).fill('FUNDER'),
                ...new Array(volFunders.length).fill('VOLFUNDER'),
                ...new Array(bundleBuyKeypairs.length).fill('BUNDLEBUY'),
                ...new Array(holderKeypairs.length).fill('HOLDER'),
                ...new Array(freezeKeypairs.length).fill('FREEZER'),
                ...new Array(mixKeypairs.length).fill('MIX'),
            ];
            console.log(allWallets);
            const uniqueKeypairsMap = new Map<string, Keypair>();
            allWallets.forEach((keypair) => {
                if (!uniqueKeypairsMap.has(keypair.publicKey.toBase58())) {
                    uniqueKeypairsMap.set(keypair.publicKey.toBase58(), keypair);
                }
            });
            const allUniqueWallets = Array.from(uniqueKeypairsMap.values());

            const allUniqueSolBalances = await getMultipleSolBalances(
                connection,
                removeKeypairDuplicates([...volumeBuyKeypairs, ...allWallets]).map((keypair) => keypair.publicKey),
            );
            const initialTotalSolBalance = allUniqueSolBalances.reduce((a, b) => a + b, 0);

            setInitialTotalSolBalance(initialTotalSolBalance);
            logMessage(`Initial total SOL balance: ${(initialTotalSolBalance / LAMPORTS_PER_SOL).toFixed(2)}`);

            setWallet(deployer[0]);
            setWalletLoaded(true);
            setFunder(funders[0]);
            console.log('volFunders:', volFunders[0].publicKey.toBase58());
            setVolFunder(volFunders[0]);
            setBundleBuyKeypairs(bundleBuyKeypairs);
            setBundleBuyValues(bundleBuyValues);
            setHolderKeypairs(holderKeypairs);
            setMixKeypairs(mixKeypairs);
            setFreezeKeypairs(freezeKeypairs);
            setAllUniqueWallets(allUniqueWallets);
            setAllWallets(allWallets);
            setAllWalletRoles(roles);
            console.log(
                'volumeBuyKeypairs',
                volumeBuyKeypairs.map((keypair) => keypair.publicKey.toBase58()),
            );
            setVolumeBuyWallets(volumeBuyKeypairs);
            setVolumeBuyValues(volumeBuyValues);
            setParsedCsvData(parsedCSV);
        } else {
            setCsvFile(null);
        }
    }
    const loadCsv = (event: React.MouseEvent) => {
        event.preventDefault();
        csvFileInput.current?.click();
    };

    useEffect(() => {
        if (csvUrlParam) {
            try {
                fetch(`/${csvUrlParam}.csv`)
                    .then((response) => response.blob())
                    .then((blob) => {
                        const csvFile = new File([blob], `${csvUrlParam}.csv`, { type: 'text/csv' });
                        console.log('Fetched CSV file:', csvFile);
                        parseCsvAndSetWallets(csvFile);
                    })
                    .catch((error) => console.error('Error fetching the CSV file:', error));
            } catch (e) {
                logMessage('Invalid CSV file!');
            }
        }
    }, [csvUrlParam]);

    const [customTokenMintAddress, setCustomTokenMintAddress] = useState<string>('');

    async function customTokenMintAddressChange(event: React.ChangeEvent<HTMLInputElement>) {
        const mint = event.target.value;
        setCustomTokenMintAddress(mint);

        try {
            injectToken(mint);
        } catch (e) {
            //logMessage('Invalid mint public key!');
        }
    }

    // Handle Private Key input
    const [privateKey, setPrivateKey] = useState<string>('');
    const privateKeyChange = (event: { target: { value: string } }) => {
        setPrivateKey(event.target.value);
    };

    const loadWallet = async () => {
        if (!privateKey) {
            logMessage('Private key is empty!', 'error');
            return;
        }
        let wallet;
        if (privateKey[0] === '[' && privateKey[privateKey.length - 1] === ']') {
            const numbers = privateKey.slice(1, -1).split(',').map(Number);
            const uint8Array = new Uint8Array(numbers);
            wallet = Keypair.fromSecretKey(uint8Array);
        } else {
            wallet = Keypair.fromSecretKey(bs58.decode(privateKey));
        }

        setWallet(wallet);
        setFunder(wallet);
        setAllUniqueWallets([wallet]);
        setAllWallets([wallet]);
        setAllWalletRoles(['DEPLOYER']);
        setWalletLoaded(true);
        logMessage('Wallet loaded successfully ' + wallet.publicKey.toString());
    };

    const extractToken = async () => {
        setDeployedToken('');
        setDeployedTokenName('');
        setCustomTokenMintAddress('');
        setPoolKeys(undefined);
        setDeployedMarketId(undefined);
    };

    return (
        <div>
            <section className='private-key'>
                <div className='load-csv-button'>
                    <button className='button-accent' onClick={loadCsv}>
                        LOAD CSV
                    </button>
                    <input type='file' accept='.csv' onChange={csvFileChange} ref={csvFileInput} />
                </div>
                <p>PK Deployer</p>
                <input type='text' value={privateKey} onChange={privateKeyChange} />
                <button className='button-accent' onClick={loadWallet}>
                    LOAD
                </button>
                {children}
            </section>

            {csvFile ? (
                <section className='load-csv-info'>
                    <div className='load-csv'>
                        <div className='loaded-info'>
                            <p>
                                <b>Token Deployer: </b>
                                {wallet ? wallet.publicKey.toBase58() : 'Wallet is not loaded!'}
                            </p>
                            <button
                                className={isTokenDeployerWalletCopied ? 'copied-button' : 'copy-button'}
                                onClick={() => copyTokenDeployerWallet(wallet ? wallet.publicKey.toBase58() : '')}
                            ></button>
                        </div>
                        <p>
                            <b>Bal: </b>
                            {(solBalance / LAMPORTS_PER_SOL).toFixed(2)}
                        </p>
                        <p>
                            <b>Total: </b>
                            {(totalWalletsBalance / LAMPORTS_PER_SOL).toFixed(2)}
                        </p>
                    </div>
                </section>
            ) : null}

            {privateKey && walletLoaded ? (
                <section className='wallet-info'>
                    <b>Loaded Wallet:</b> <p>{wallet.publicKey.toBase58()}</p>
                    <button
                        className={isLoadedWalletCopied ? 'copied-button' : 'copy-button'}
                        onClick={() => copyLoadedWallet(wallet.publicKey.toBase58())}
                    ></button>
                </section>
            ) : null}

            {!csvFile && !walletLoaded ? (
                <section className='load-info'>
                    <p>⏳ Please Load CSV or Private Key</p>
                    {!deployedToken && (
                        <div className='token-mint-container'>
                            <input
                                type='text'
                                placeholder='Or Set Custom Token Mint Address'
                                value={customTokenMintAddress}
                                onChange={customTokenMintAddressChange}
                            />
                        </div>
                    )}
                </section>
            ) : null}

            {deployedToken ? (
                <section className='load-csv-info'>
                    <div className='load-csv'>
                        <div className='loaded-info'>
                            <p>
                                <b>Minted Token: </b>
                                {deployedToken}
                            </p>
                            <button className={isMintedTokenCopied ? 'copied-button' : 'copy-button'} onClick={() => copyMintedToken(deployedToken)}></button>
                            <button className='remove-mint' onClick={extractToken}></button>
                        </div>
                        <p>
                            <b>
                                {deployedTokenName} {deployedTokenSymbol}
                            </b>
                        </p>
                        <p>
                            <button
                                onClick={() => {
                                    window.open(`https://dexscreener.com/solana/${deployedToken}`, '_blank');
                                }}
                                style={{ background: 'none', border: 'none', padding: '0' }}
                            >
                                <img src='/dexlogo.png' alt='Button Img' style={{ width: '50px', height: 'auto' }} />
                            </button>
                        </p>
                    </div>
                </section>
            ) : null}

            {!deployedToken && (csvFile || walletLoaded) ? (
                <div className='token-mint-container'>
                    <input type='text' placeholder='Or Set Custom Token Mint Address' value={customTokenMintAddress} onChange={customTokenMintAddressChange} />
                </div>
            ) : null}
        </div>
    );
}

export default LoadWallet;
